import { useState } from 'react';
import { parties } from '../api/endpoints';
import type { ActualizeResult, ClientAccount, ClientCard, Party } from '../api/types';
import type { Column } from '../components/ui';
import { Card, DataTable, ErrorBox, KV, Loading, PageHeader, StatusBadge } from '../components/ui';

import { useAsync } from '../hooks';

/** Отображаемый статус актуализации реквизитов. */
function actualStatus(s?: string | null, at?: string | null): string {
  const base = s === 'ok' ? 'актуально' : s === 'changed' ? 'расхождения!'
    : s === 'error' ? 'ошибка проверки' : s === 'unsupported' ? 'нет реестра'
      : 'не проверялось';
  const stale = at && (Date.now() - Date.parse(at)) > 30 * 864e5 ? ' · устарело' : '';
  return base + stale + (at ? ` (${at.slice(0, 10)})` : '');
}

/** Кнопка «Актуализировать по ИНН» — POST party/<id>/actualize/. */
function ActualizeButton({ partyId, disabled, onDone }: {
  partyId: string; disabled?: boolean;
  onDone: (r: ActualizeResult) => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button className="btn small" disabled={disabled || busy}
            onClick={async () => {
              setBusy(true);
              try { onDone(await parties.actualizeParty(partyId)); }
              finally { setBusy(false); }
            }}>
      {busy ? 'Проверяю…' : 'Актуализировать'}</button>
  );
}

/** Блок результата актуализации: реестр + расхождения. */
function ActualizeResultBox({ r }: { r: ActualizeResult }) {
  const keys = Object.keys(r.diffs ?? {});
  return (
    <div style={{ margin: '8px 0', padding: 8, border: '1px solid var(--border, #ddd)',
                  borderRadius: 6, fontSize: 13 }}>
      <div>
        <strong>{r.status === 'ok' ? 'Актуально' :
          r.status === 'changed' ? 'Есть расхождения' :
            r.status === 'error' ? 'Ошибка проверки' : 'Реестр не подключён'}</strong>
        {r.registry_name && <span className="muted"> · реестр: {r.registry_name}</span>}
        {r.registry_state && <span className="muted"> · {r.registry_state}</span>}
      </div>
      {r.error && <div style={{ color: '#b91c1c' }}>{r.error}</div>}
      {keys.map((k) => (
        <div key={k} style={{ marginTop: 4 }}>
          <span className="muted">{k}:</span> карточка «{r.diffs![k].current || '—'}»
          → реестр «{r.diffs![k].registry}»
        </div>
      ))}
    </div>
  );
}

/** Карточка клиента: реквизиты, плательщики, контакты. */
function ClientCardPanel({ accountId }: { accountId: string }) {
  const card = useAsync<ClientCard>(() => parties.clientAccountCard(accountId), [accountId]);
  const partyList = useAsync<Party[]>(() => parties.list(), []);
  const [newPayer, setNewPayer] = useState('');
  // mode='existing' — выбрать контрагента; 'new' — создать нового плательщика
  const [payerMode, setPayerMode] = useState<'existing' | 'new'>('existing');
  const [npName, setNpName] = useState('');
  const [npTax, setNpTax] = useState('');
  const [npCountry, setNpCountry] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Последний результат актуализации по party_id (клиент и плательщики).
  const [actResults, setActResults] = useState<Record<string, ActualizeResult>>({});

  if (card.loading && !card.data) return <Loading text="Загрузка карточки клиента…" />;
  if (!card.data) return <ErrorBox error={card.error} onRetry={card.reload} />;

  const p = card.data.party;
  const payerIds = new Set(card.data.payers.map((l) => l.payer));
  const candidates = (partyList.data ?? []).filter(
    (x) => x.id !== accountId && !payerIds.has(x.id));

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try { await fn(); card.reload(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Ошибка'); }
    finally { setBusy(false); }
  };

  return (
    <Card title={`Клиент: ${p.name}`}>
      <div className="kv-grid">
        <KV k="ИНН/БИН" v={p.tax_id || '—'} />
        <KV k="Рег. номер" v={p.registration_id || '—'} />
        <KV k="Страна" v={p.country || '—'} />
        <KV k="Юр. адрес" v={p.legal_address || '—'} />
        <KV k="КПП" v={p.kpp || '—'} />
        <KV k="Подписант" v={p.signatory_name || '—'} />
        <KV k="Активен" v={p.active === false ? 'Нет' : 'Да'} />
        <KV k="Актуализация" v={
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <span style={p.actualization_status === 'changed'
                         ? { color: '#b91c1c', fontWeight: 600 } : undefined}>
              {actualStatus(p.actualization_status, p.actualized_at)}
            </span>
            <ActualizeButton partyId={p.id} disabled={!p.tax_id}
              onDone={(r) => { setActResults({ ...actResults, [p.id]: r }); card.reload(); }} />
          </span>} />
      </div>
      {actResults[p.id] && <ActualizeResultBox r={actResults[p.id]} />}

      <h4 style={{ margin: '16px 0 6px' }}>Плательщики ({card.data.payers.length})</h4>
      {card.data.payers.length === 0 && (
        <div className="muted">Плательщики не подвязаны. Добавьте вручную —
          или они появятся автоматически, когда в заявке клиента укажут
          плательщика.</div>)}
      {card.data.payers.length > 0 && (
        <DataTable
          columns={[
            { key: 'name', title: 'Название', render: (l) => <strong>{l.name}</strong> },
            { key: 'tax_id', title: 'ИНН/БИН', render: (l) => l.tax_id || '—' },
            { key: 'country', title: 'Страна', render: (l) => l.country || '—' },
            {
              key: 'actzn', title: 'Актуализация', render: (l) => (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center',
                               fontSize: 12 }}>
                  <span style={l.actualization_status === 'changed'
                               ? { color: '#b91c1c' } : undefined}>
                    {actualStatus(l.actualization_status, l.actualized_at)}
                  </span>
                  <ActualizeButton partyId={l.payer} disabled={!l.tax_id}
                    onDone={(r) => { setActResults({ ...actResults, [l.payer]: r }); card.reload(); }} />
                </span>
              ),
            },
            {
              key: 'def', title: 'Основной', render: (l) => l.is_default
                ? <StatusBadge value="default" /> : '—',
            },
            {
              key: 'act', title: '', width: '150px', render: (l) => (
                <span style={{ display: 'flex', gap: 6 }}>
                  {!l.is_default && (
                    <button className="btn small" disabled={busy}
                            onClick={() => run(() => parties.setClientPayerDefault(
                              l.id, l.revision ?? 1, true))}>
                      Сделать основным</button>)}
                  <button className="btn small" disabled={busy}
                          onClick={() => run(() => parties.removeClientPayer(l.id))}>
                    Отвязать</button>
                </span>
              ),
            },
          ] as Column<ClientCard['payers'][number]>[]}
          rows={card.data.payers}
        />
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
        <select className="input" style={{ width: 170 }} value={payerMode}
                onChange={(e) => setPayerMode(e.target.value as 'existing' | 'new')}>
          <option value="existing">Существующий</option>
          <option value="new">Новый плательщик</option>
        </select>
        {payerMode === 'existing' ? (
          <>
            <select className="input" style={{ minWidth: 300 }} value={newPayer}
                    onChange={(e) => setNewPayer(e.target.value)}>
              <option value="">— выбрать контрагента —</option>
              {candidates.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}{x.tax_id ? ` (ИНН ${x.tax_id})` : ''}
                </option>
              ))}
            </select>
            <button className="btn small primary" disabled={busy || !newPayer}
                    onClick={() => run(() => parties.addClientPayer(accountId, newPayer))}>
              + Плательщик</button>
          </>
        ) : (
          <>
            <input className="input" style={{ minWidth: 220 }} placeholder="Название *"
                   value={npName} onChange={(e) => setNpName(e.target.value)} />
            <input className="input" style={{ width: 140 }} placeholder="ИНН/БИН"
                   value={npTax} onChange={(e) => setNpTax(e.target.value)} />
            <input className="input" style={{ width: 70 }} placeholder="RU"
                   maxLength={2} value={npCountry}
                   onChange={(e) => setNpCountry(e.target.value.toUpperCase())} />
            <button className="btn small primary" disabled={busy || !npName.trim()}
                    onClick={() => run(async () => {
                      const party = await parties.createParty({
                        name: npName.trim(), kind: 'company',
                        tax_id: npTax.trim(), country: npCountry.trim(),
                      });
                      await parties.addClientPayer(accountId, party.id);
                      setNpName(''); setNpTax(''); setNpCountry('');
                      partyList.reload();
                    })}>
              Создать и подвязать</button>
          </>
        )}
      </div>
      {card.data.payers.filter((l) => actResults[l.payer]).map((l) => (
        <ActualizeResultBox key={l.payer} r={actResults[l.payer]} />
      ))}

      <h4 style={{ margin: '16px 0 6px' }}>Контакты ({card.data.contacts.length})</h4>
      {card.data.contacts.length === 0 && (
        <div className="muted">Контактов нет — добавляются в карточке стороны
          или резолвятся из писем.</div>)}
      {card.data.contacts.map((c) => (
        <div key={c.id} className="kv-grid" style={{ marginBottom: 4 }}>
          <KV k={c.position || 'Контакт'} v={
            <span>
              <strong>{c.name}</strong>
              {c.points.length > 0 && (
                <span className="muted">
                  {' — '}
                  {c.points.map((pt) => `${pt.type === 'email' ? '✉' : '☎'} ${pt.value}`).join('  ·  ')}
                </span>
              )}
            </span>} />
        </div>
      ))}
      {error && <div className="muted" style={{ color: '#b91c1c' }}>{error}</div>}
    </Card>
  );
}

/** Форма создания клиента: Party + клиентский счёт + контакт (опц.). */
function CreateClientForm({ onCreated, onCancel }: {
  onCreated: (accountId: string) => void; onCancel: () => void;
}) {
  const [f, setF] = useState({
    name: '', tax_id: '', country: 'RU', registration_id: '',
    legal_address: '', kpp: '', signatory_name: '',
    contact_name: '', contact_position: '',
    contact_email: '', contact_phone: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const party = await parties.createParty({
        name: f.name.trim(), kind: 'company',
        tax_id: f.tax_id.trim(), country: f.country.trim().toUpperCase(),
        registration_id: f.registration_id.trim(),
        legal_address: f.legal_address.trim(), kpp: f.kpp.trim(),
        signatory_name: f.signatory_name.trim(),
      });
      const account = await parties.createClientAccount(party.id);
      if (f.contact_name.trim()) {
        const contact = await parties.createContact({
          name: f.contact_name.trim(), party: party.id,
          position: f.contact_position.trim(),
        });
        if (f.contact_email.trim())
          await parties.createContactPoint(contact.id, 'email', f.contact_email.trim());
        if (f.contact_phone.trim())
          await parties.createContactPoint(contact.id, 'phone', f.contact_phone.trim());
      }
      onCreated(account.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать клиента');
    } finally {
      setBusy(false);
    }
  };

  const field = (k: keyof typeof f, ph: string, width = 180) => (
    <input className="input" style={{ width }} placeholder={ph}
           value={f[k]} onChange={set(k)} />
  );

  return (
    <Card title="Новый клиент">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {field('name', 'Название компании *', 260)}
        {field('tax_id', 'ИНН/БИН', 140)}
        {field('country', 'Страна (RU)', 90)}
        {field('registration_id', 'Рег. номер (ОГРН)', 150)}
        {field('legal_address', 'Юр. адрес', 260)}
        {field('kpp', 'КПП', 90)}
        {field('signatory_name', 'Подписант', 180)}
      </div>
      <div className="muted" style={{ margin: '6px 0' }}>Контакт (необязательно):</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {field('contact_name', 'Имя контакта', 200)}
        {field('contact_position', 'Должность', 140)}
        {field('contact_email', 'Email', 200)}
        {field('contact_phone', 'Телефон', 160)}
      </div>
      {error && <div className="muted" style={{ color: '#b91c1c', marginTop: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn primary" disabled={busy || !f.name.trim()} onClick={submit}>
          Создать клиента</button>
        <button className="btn" disabled={busy} onClick={onCancel}>Отмена</button>
      </div>
    </Card>
  );
}

export function ClientsPage() {
  const list = useAsync<ClientAccount[]>(() => parties.clientAccounts(), []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const columns: Column<ClientAccount>[] = [
    { key: 'name', title: 'Клиент', render: (r) => <strong>{r.party_name ?? r.party.slice(0, 8)}</strong> },
    { key: 'tax', title: 'ИНН/БИН', render: (r) => r.party_tax_id || '—' },
    { key: 'country', title: 'Страна', render: (r) => r.party_country || '—' },
    { key: 'payers', title: 'Плательщики', width: '110px',
      render: (r) => <strong>{r.payers_count ?? 0}</strong> },
    { key: 'created', title: 'Создан', width: '120px',
      render: (r) => (r.created_at ?? '').slice(0, 10) },
  ];

  return (
    <div>
      <PageHeader title="Клиенты">
        <button className="btn primary" onClick={() => setCreating(!creating)}>
          {creating ? 'Скрыть форму' : '+ Клиент'}</button>
        <button className="btn" onClick={list.reload}>Обновить</button>
      </PageHeader>
      <ErrorBox error={list.error} />
      {creating && (
        <CreateClientForm
          onCreated={(id) => { setCreating(false); setSelectedId(id); list.reload(); }}
          onCancel={() => setCreating(false)} />
      )}
      <Card title="Клиентские счета">
        <DataTable columns={columns} rows={list.data ?? []}
               onRowClick={(r) => setSelectedId(r.id === selectedId ? null : r.id)} />
        {(list.data ?? []).length === 0 && !list.loading && (
          <div className="muted" style={{ marginTop: 8 }}>
            Клиенты появляются автоматически при создании заявок из входящих.
          </div>)}
      </Card>
      {selectedId && <ClientCardPanel accountId={selectedId} />}
    </div>
  );
}
